import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

const STATUS_LABEL = {
  open: { text: 'Open', color: 'var(--text-muted)' },
  matched: { text: 'Found — we have it', color: 'var(--success)' },
  fulfilled: { text: 'Fulfilled', color: 'var(--success)' },
  cancelled: { text: 'Cancelled', color: 'var(--danger)' },
};

const blank = { raw_text: '', card_name: '', set_name: '', condition: '', max_price: '' };

// "Looking for" list. A customer describes cards/product they want; when we
// upload matching inventory the admin surfaces + (later) notifies them.
//
// NOTE (stub): customers aren't emailed yet when status flips to "matched" —
// that send needs Resend (see PLAN.md). Until then the status change is visible
// right here when they next open the tab.
export default function RequestsTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    if (!user) return;
    supabase
      .from('want_list_items')
      .select('id, raw_text, card_name, set_name, condition, max_price_cents, status, created_at, matched_product_id, products:matched_product_id(name, slug)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => { setRows(data || []); setLoading(false); });
  };

  useEffect(load, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.raw_text.trim()) { setError('Tell us what you’re looking for.'); return; }
    setSaving(true);
    const maxCents = form.max_price.trim() ? Math.round(parseFloat(form.max_price) * 100) : null;
    const { error: err } = await supabase.from('want_list_items').insert({
      user_id: user.id,
      raw_text: form.raw_text.trim(),
      card_name: form.card_name.trim() || null,
      set_name: form.set_name.trim() || null,
      condition: form.condition.trim() || null,
      max_price_cents: Number.isFinite(maxCents) ? maxCents : null,
    });
    setSaving(false);
    if (err) { setError(err.message); return; }
    setForm(blank);
    setShowForm(false);
    setLoading(true);
    load();
  };

  const cancelRequest = async (id) => {
    await supabase.from('want_list_items').update({ status: 'cancelled', updated_at: new Date().toISOString() }).eq('id', id);
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'cancelled' } : r)));
  };

  return (
    <div>
      <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginTop: 0, marginBottom: 16, lineHeight: 1.6 }}>
        Looking for something we don't have listed? Add it here. When we get it in, it'll show as found
        (and we'll email you once notifications are switched on).
      </p>

      {!showForm && (
        <button onClick={() => setShowForm(true)} className="sv-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 20 }}>
          <Plus size={16} /> Add a request
        </button>
      )}

      {showForm && (
        <form onSubmit={submit} className="sv-card" style={{ padding: 16, marginBottom: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <textarea
            className="sv-input"
            placeholder="What are you looking for? (e.g. Charizard VMAX, Japanese, near mint)"
            value={form.raw_text}
            onChange={(e) => setForm({ ...form, raw_text: e.target.value })}
            rows={2}
            style={{ resize: 'vertical' }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input className="sv-input" placeholder="Card name (optional)" value={form.card_name} onChange={(e) => setForm({ ...form, card_name: e.target.value })} />
            <input className="sv-input" placeholder="Set (optional)" value={form.set_name} onChange={(e) => setForm({ ...form, set_name: e.target.value })} />
            <input className="sv-input" placeholder="Condition (optional)" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} />
            <input className="sv-input" type="number" step="0.01" min="0" placeholder="Max price $ (optional)" value={form.max_price} onChange={(e) => setForm({ ...form, max_price: e.target.value })} />
          </div>
          {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" disabled={saving} className="sv-btn">{saving ? 'Saving…' : 'Save request'}</button>
            <button type="button" onClick={() => { setShowForm(false); setForm(blank); setError(''); }} className="sv-btn-outline">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>No requests yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => {
            const status = STATUS_LABEL[r.status] || STATUS_LABEL.open;
            return (
              <div key={r.id} className="sv-card" style={{ padding: '13px 15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{r.card_name || r.raw_text}</div>
                    {r.card_name && (
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>{r.raw_text}</div>
                    )}
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {r.set_name && <span>{r.set_name}</span>}
                      {r.condition && <span>{r.condition}</span>}
                      {r.max_price_cents != null && <span>up to {money(r.max_price_cents)}</span>}
                    </div>
                    {r.status === 'matched' && r.products && (
                      <a href={`/product/${r.products.slug}`} style={{ fontSize: '0.82rem', color: 'var(--accent)', fontWeight: 700, textDecoration: 'underline', display: 'inline-block', marginTop: 6 }}>
                        View {r.products.name}
                      </a>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span style={{ fontSize: '0.76rem', fontWeight: 700, color: status.color }}>{status.text}</span>
                    {(r.status === 'open' || r.status === 'matched') && (
                      <button
                        type="button"
                        onClick={() => cancelRequest(r.id)}
                        aria-label="Cancel request"
                        style={{ display: 'flex', marginLeft: 'auto', marginTop: 8, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
